import { classNames, extend } from '@dimjs/utils';
import { getUuid, imageCompressToFile, TImageCompressOptions, toArray, TPlainObject } from '@flatbiz/utils';
import { hooks } from '@wove/react';
import { CSSProperties, FC, Fragment, InputHTMLAttributes, ReactElement, useMemo, useRef } from 'react';
import { dialogMessage } from '../dialog';
import { Grid, GridItem, GridProps } from '../grid';
import { useEffectCustom } from '../hooks';
import { If } from '../if';
import { ImageViewer } from '../image-viewer';
import { Spinner } from '../spinner';
import { CommonPropsWithChildren, withNativeProps } from '../utils/native-props';
import { UploadTrigger } from './compts';
import './style.less';

type TaskStatus = 'pending' | 'fail' | 'success';

interface UploadListFileItem {
  url: string;
  thumbnailUrl: string;
  fileName: string;
}

export type UploadListProps = {
  /**
   * 属性映射
   */
  attributeMapping?: {
    url: string;
    fileName?: string;
    // 缩略图
    thumbnailUrl?: string;
  };
  value?: TPlainObject[];
  onChange?: (fileList?: TPlainObject[]) => void;
  accept?: string;
  multiple?: boolean;
  maxCount?: number;
  onCountExceed?: (exceed: number) => void;
  disabled?: boolean;
  showUpload?: boolean;
  deletable?: boolean;
  capture?: InputHTMLAttributes<unknown>['capture'];
  beforeUpload?: (file: File[]) => Promise<File[]> | File[];
  onUpload: (file: File) => Promise<TPlainObject>;
  onDelete?: (file: TPlainObject) => boolean | Promise<boolean> | void;
  gridColumns?: number;
  gridGap?: GridProps['gap'];
  showList?: boolean;
  showFileName?: boolean;
  uploadTriggerDesc?: string;
  uplaodFailRender?: ReactElement;
  uploadLoadingRender?: ReactElement;
  onClickFile?: (fileItem: TPlainObject, fileList: TPlainObject[]) => void;
  /** 图片压缩配置 */
  imageCompressOptions?: TImageCompressOptions;
} & CommonPropsWithChildren<{ '--upload-list-image-height': CSSProperties['height'] }>;

const classPrefix = `v-upload-list`;

type Task = {
  id: number | string;
  url?: string;
  thumbnailUrl?: string;
  file: File;
  status: TaskStatus;
  fileName?: string;
  respData: TPlainObject;
};

/**
 * 图片上传
 * @param props
 * @returns
 * ```
 * 1. 批量上传时，出现第一个上传异常后面全部都是异常
 * 2. attributeMapping页面显示要素属性名称映射
 * 3. 如果想使用其他上传方式，可设置disabled，然后再UploadList children中自行处理点击事件
 * ```
 */
export const UploadList: FC<UploadListProps> = (props) => {
  const maxCount = props.maxCount;
  const attributeMapping = useMemo(() => {
    return extend(
      {
        url: 'url',
        fileName: 'fileName',
        thumbnailUrl: 'thumbnailUrl',
      },
      props.attributeMapping,
    ) as UploadListFileItem;
  }, [props.attributeMapping]);

  const forceUpdate = hooks.useForceUpdate();
  const tasksRef = useRef<Task[]>([]);
  const onceSelectCount = useRef<number>(0);
  const onceSelectUploadCount = useRef<number>(0);

  const getUrlFileName = hooks.useCallbackRef((item) => {
    return {
      url: item[attributeMapping.url],
      fileName: item[attributeMapping.fileName],
      thumbnailUrl: item[attributeMapping.thumbnailUrl],
    };
  });

  useEffectCustom(() => {
    const values = toArray(props.value);
    if (values.length > 0) {
      values.forEach((item) => {
        if (!tasksRef.current.find((temp) => temp.id === item?.['id'])) {
          const { url, fileName, thumbnailUrl } = getUrlFileName(item);
          tasksRef.current.push({
            id: getUuid(),
            url,
            thumbnailUrl,
            status: 'success',
            fileName,
            respData: item?.['respData'],
          } as unknown as Task);
        }
      });
    } else {
      tasksRef.current = [];
    }
    forceUpdate();
  }, [props.value]);

  const onChange = hooks.useCallbackRef(() => {
    const successList = getSuccessTaskList().map((item) => {
      return {
        id: item.id,
        [`${attributeMapping.url}`]: item.url,
        [`${attributeMapping.fileName}`]: item.fileName,
        [`${attributeMapping.thumbnailUrl}`]: item.thumbnailUrl,
        respData: item.respData,
      };
    });
    props.onChange?.(successList.length > 0 ? successList : undefined);
  });

  const onUploadHandle = hooks.useCallbackRef((status, task, respData?) => {
    tasksRef.current.map((item) => {
      if (item.id === task.id) {
        if (status === 'success' && respData) {
          const { url, thumbnailUrl } = getUrlFileName(respData);
          item.url = url;
          item.thumbnailUrl = thumbnailUrl;
          item.status = 'success';
          item.respData = respData;
        }
        if (status === 'fail') {
          item.status = 'fail';
        }
      }
    });
  });

  const onUploadFinally = hooks.useCallbackRef(() => {
    onceSelectUploadCount.current += 1;
    forceUpdate();
    if (onceSelectUploadCount.current === onceSelectCount.current) {
      onChange();
      return;
    }
  });

  const uploadTask = hooks.useCallbackRef((taskList: Task[]) => {
    onceSelectUploadCount.current = 0;
    onceSelectCount.current = taskList.length;
    taskList.map(async (task) => {
      const compressFile = await imageCompressToFile(task.file, props.imageCompressOptions);
      props
        .onUpload(compressFile)
        .then((item) => {
          onUploadHandle('success', task, item);
          return item;
        })
        .catch(() => {
          onUploadHandle('fail', task);
        })
        .finally(onUploadFinally);
    });
  });

  const onCountExceed = hooks.useCallbackRef((exceed: number) => {
    if (props.onCountExceed) {
      props.onCountExceed(exceed);
    } else {
      console.warn(`本次上传有${exceed}张图片超设置最大数量上线`);
      dialogMessage(`上传成功数量不能超过${maxCount}张`);
    }
  });

  const getSuccessTaskList = hooks.useCallbackRef(() => {
    return tasksRef.current.filter((item) => item.status === 'success');
  });

  const getNotErrorTaskList = hooks.useCallbackRef(() => {
    return tasksRef.current.filter((item) => item.status !== 'fail');
  });

  const onInputChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    e.persist();
    const { files: rawFiles } = e.target;
    if (!rawFiles) return;
    let files = [].slice.call(rawFiles) as File[];
    if (props.beforeUpload) {
      files = await props.beforeUpload(files);
    }
    if (files.length === 0) {
      return;
    }
    if (maxCount && maxCount > 0) {
      const notErrorTaskList = getNotErrorTaskList();
      const exceed = notErrorTaskList.length + files.length - maxCount;
      if (exceed > 0) {
        files = files.slice(0, files.length - exceed);
        onCountExceed(exceed);
      }
    }
    const newTasks = files.map((file) => {
      return {
        id: getUuid(),
        status: 'pending',
        file,
        fileName: file.name.substring(0, file.name.lastIndexOf('.')),
      } as Task;
    });

    tasksRef.current = tasksRef.current.concat(newTasks);
    void uploadTask(newTasks);
    e.target.value = ''; // HACK: fix the same file doesn't trigger onChange
    forceUpdate();
  };

  const onDeleteTask = hooks.useCallbackRef((task: Task) => {
    if (props.disabled) return;
    const targetIndex = tasksRef.current.findIndex((item) => item.id === task.id);
    if (targetIndex >= 0) {
      tasksRef.current.splice(targetIndex, 1);
    }
    forceUpdate();
    if (task.status === 'success') {
      onChange();
    }
  });

  const onAgainUpload = hooks.useCallbackRef(async (task: Task) => {
    const notErrorTaskList = getNotErrorTaskList();
    if (maxCount && maxCount > 0 && notErrorTaskList.length >= maxCount) {
      onCountExceed(1);
      return;
    }
    if (props.beforeUpload) {
      await props.beforeUpload([task.file]);
    }
    task.status = 'pending';
    void uploadTask([task]);
    forceUpdate();
  });

  const onClickImage = hooks.useCallbackRef((item: Task, index: number) => {
    if (item.status === 'fail') {
      void onAgainUpload(item);
    } else if (item.status === 'success' && item.url) {
      if (props.onClickFile) {
        const respDataList = getSuccessTaskList().map((temp) => temp.respData);
        props.onClickFile(item.respData, respDataList);
      } else {
        ImageViewer.show({
          images: tasksRef.current.map((item) => item.url as string),
          initActiveIndex: index,
        });
      }
    }
  });

  const className = classNames(classPrefix, {
    [`${classPrefix}-disabled`]: props.disabled,
  });

  const dynamicShowUpload = maxCount && maxCount > 0 ? getSuccessTaskList().length < maxCount : true;

  return withNativeProps(
    props,
    <div className={className}>
      <Grid columns={props.gridColumns || 4} gap={props.gridGap}>
        <If isTrue={!!props.showList}>
          {tasksRef.current.map((item, index) => {
            const isPending = item.status === 'pending';
            const isFailed = item.status === 'fail';
            const imageUrl = item.file ? URL.createObjectURL(item.file) : item.thumbnailUrl || item.url;
            return (
              <GridItem key={item.id}>
                <Fragment>
                  <div
                    className={`${classPrefix}-content  ${classPrefix}-${item.status}`}
                    onClick={onClickImage.bind(null, item, index)}
                  >
                    <div className={`${classPrefix}-image`} style={{ backgroundImage: `url(${imageUrl})` }} />
                    {isFailed &&
                      (props.uplaodFailRender ? (
                        props.uplaodFailRender
                      ) : (
                        <div className={`${classPrefix}-fail-content`}>
                          <div>上传失败</div>
                          <div>重新上传</div>
                        </div>
                      ))}
                    {isPending &&
                      (props.uploadLoadingRender ? props.uploadLoadingRender : <Spinner spinning={true} />)}
                  </div>
                  {props.deletable && !props.disabled && (
                    <div className={`${classPrefix}-image-close`} onClick={onDeleteTask.bind(null, item)} />
                  )}
                  {props.showFileName && item.fileName ? (
                    <div className={`${classPrefix}-image-name`}>{item.fileName}</div>
                  ) : null}
                </Fragment>
              </GridItem>
            );
          })}
        </If>
        {props.showUpload && dynamicShowUpload && (
          <GridItem>
            <UploadTrigger
              disabled={props.disabled}
              capture={props.capture}
              accept={props.accept}
              multiple={props.multiple}
              uploadTriggerDesc={props.uploadTriggerDesc}
              onChange={onInputChange}
            >
              {props.children}
            </UploadTrigger>
          </GridItem>
        )}
      </Grid>
    </div>,
  );
};

UploadList.defaultProps = {
  disabled: false,
  deletable: true,
  showUpload: true,
  multiple: false,
  accept: 'image/*',
  showList: true,
  gridGap: 20,
};
